import type { ConfigurationParameters, FetchParams, ResponseContext } from '../generated-api';
import {
  CancelPaymentControllerApi,
  Configuration,
  F1ControllerApi,
  ListActivePaymentControllerApi,
  LocationControllerApi,
  MoveOrderLinesControllerApi,
  PaymentPollingControllerApi,
  PendingPaymentControllerApi,
  ReceiptControllerApi,
  SplitPaymentControllerApi,
  SubmitCartControllerApi,
} from '../generated-api';
import config from './config';
import { useAuth } from './context/AuthContext';
import { useCurrentClientVersion, useLatestClientVersion, useVersionActions } from './context/VersionStore';

export class ApiError extends Error {
  public traceId?: string;
  public title?: string;

  constructor(public response: Response, public errorBody?: unknown, message?: string, title?: string) {
    super(message || 'API call failed');
    this.name = 'ApiError';
    this.traceId = response.headers.get('x-trace-id') || undefined;
    this.title = title || 'Error';
  }
}

export const useApiClient = () => {
  const { getAccessToken } = useAuth();

  const latestClientVersion = useLatestClientVersion();
  const currentClientVersion = useCurrentClientVersion();
  const { setLatestClientVersion } = useVersionActions();

  const pre = async (context: ResponseContext): Promise<FetchParams | void> => {
    const accessToken = await getAccessToken();
    return {
      init: {
        ...context.init,
        headers: new Headers({
          ...context.init.headers,
          'x-client-version': currentClientVersion.toString(),
          Authorization: `Bearer ${accessToken}`,
        }),
      },
      url: context.url,
    };
  };
  const afterRequest = async (res: ResponseContext) => {
    const newLatestClientVersion = res.response.headers.get('x-latest-client-version');

    // Update latest available app version, if our local state is out of sync
    if (newLatestClientVersion && newLatestClientVersion !== latestClientVersion?.toString()) {
      const parsedVersion = Number.parseInt(newLatestClientVersion ?? '', 10);
      if (!Number.isNaN(parsedVersion)) {
        setLatestClientVersion(parsedVersion);
      }
    }

    if (!res.response.ok) {
      // Parse error details safely before rejecting
      const errorBody = await res.response.json().catch(() => null);

      const errorMessage =
        errorBody?.message || errorBody?.description || `HTTP ${res.response.status}: ${res.response.statusText}`;

      throw new ApiError(res.response, errorBody, errorMessage);
    }

    return res.response;
  };

  const configParams: ConfigurationParameters = {
    basePath: config.bff,
    middleware: [{ post: afterRequest, pre }],
  };
  const apiConfig = new Configuration(configParams);

  return {
    F1ControllerApi: new F1ControllerApi(apiConfig),
    ListActivePaymentController: new ListActivePaymentControllerApi(apiConfig),
    LocationControllerApi: new LocationControllerApi(apiConfig),
    MoveOrderLinesControllerApi: new MoveOrderLinesControllerApi(apiConfig),
    PaymentCancelledController: new CancelPaymentControllerApi(apiConfig),
    PaymentPollingController: new PaymentPollingControllerApi(apiConfig),
    PendingPaymentControllerApi: new PendingPaymentControllerApi(apiConfig),
    ReceiptControllerApi: new ReceiptControllerApi(apiConfig),
    SplitPaymentControllerApi: new SplitPaymentControllerApi(apiConfig),
    SubmitCartControllerApi: new SubmitCartControllerApi(apiConfig),
  };
};
